import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import {
  Select,
  ColorPicker,
  Button,
  Tooltip,
  Radio,
  RadioChangeEvent,
} from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTrash,
  faBold,
  faItalic,
  faUnderline,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
} from "@fortawesome/free-solid-svg-icons"
import { Color } from "antd/es/color-picker"

import ResizableElementWrapper from "./shared/ResizableElementWrapper.component"
import { DEFAULT_TEXT_SIZES } from "constants/dashboardElementSizes"
import {
  SlideElement,
  TEXT_ALIGN,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"

const { Option } = Select

/**
 * Font size option interface
 */
interface FontSizeOption {
  value: number
  label: string
}

/**
 * Color preset interface for Ant Design ColorPicker
 */
interface ColorPreset {
  label: string
  colors: string[]
}

/**
 * Element update payload for text elements
 */
interface TextElementUpdate {
  text?: string
  fontSize?: number
  color?: string
  isBold?: boolean
  isItalic?: boolean
  isUnderline?: boolean
  textAlign?: string
  width?: number
  height?: number
}

/**
 * Props for TextElement component
 */
export interface TextElementProps {
  /** Element object from parent (id, type, text, x, y, width, height, styles...) */
  element: SlideElement
  /** Callback to update element props in parent */
  onUpdate: (elementId: string, updates: TextElementUpdate) => void
  /** Flag to disable editing */
  readonly?: boolean
  /** Callback to request deletion of this element */
  onDeleteRequest?: () => void
}

// Define font size options statically outside the component
const FONT_SIZE_OPTIONS: FontSizeOption[] = [
  { value: 12, label: "12" },
  { value: 14, label: "14" },
  { value: 16, label: "16" },
  { value: 20, label: "20" },
  { value: 24, label: "24" },
  { value: 32, label: "32" },
  { value: 40, label: "40" },
]

// Define recommended color presets statically
const COLOR_PRESETS: ColorPreset[] = [
  {
    label: "Recommended",
    colors: [
      "#000000",
      "#FFFFFF",
      "#1677FF",
      "#FF4D4F",
      "#52C41A",
      "#722ED1",
      "#FA8C16",
      "#EB2F96",
    ],
  },
]

/**
 * Renders a text or heading element within the ReportingCanvas.
 * Allows inline editing of text content and styling (font size, color, bold, italic, underline, alignment).
 * Handles resizing via drag handles provided by ResizableElementWrapper.
 *
 * @param props - Component props
 * @returns JSX element for text editing and display
 *
 * @example
 * <TextElement
 *   element={textElement}
 *   onUpdate={handleUpdate}
 *   readonly={false}
 *   onDeleteRequest={handleDelete}
 * />
 */
const TextElement: React.FC<TextElementProps> = ({
  element,
  onUpdate,
  readonly = false,
  onDeleteRequest,
}) => {
  // --- State ---
  const [isEditing, setIsEditing] = useState<boolean>(false) // Is the element currently in edit mode?
  // Local state for text content and styles *during* editing
  const [text, setText] = useState<string>(element.text || "")
  const [fontSize, setFontSize] = useState<number>(
    element.fontSize || (element.type === ELEMENT_TYPE.HEADING ? 24 : 16)
  )
  const [color, setColor] = useState<string>(element.color || "#000000")
  const [isBold, setIsBold] = useState<boolean>(
    element.isBold || element.type === ELEMENT_TYPE.HEADING
  )
  const [isItalic, setIsItalic] = useState<boolean>(element.isItalic || false)
  const [isUnderline, setIsUnderline] = useState<boolean>(
    element.isUnderline || false
  )
  const [textAlign, setTextAlign] = useState<string>(
    element.textAlign || TEXT_ALIGN.LEFT
  )
  // State to control the visibility of the Ant Design ColorPicker dropdown
  const [colorPickerOpen, setColorPickerOpen] = useState<boolean>(false)

  // --- Refs ---
  const inputRef = useRef<HTMLInputElement>(null) // Ref for the input element during editing

  // --- Effects ---
  // Effect to update local editing state when element props change from the parent,
  // but only if the element is *not* currently being edited by the user.
  useEffect(() => {
    if (!isEditing) {
      setText(element.text || "")
      setFontSize(
        element.fontSize || (element.type === ELEMENT_TYPE.HEADING ? 24 : 16)
      )
      setColor(element.color || "#000000")
      setIsBold(element.isBold || element.type === ELEMENT_TYPE.HEADING)
      setIsItalic(element.isItalic || false)
      setIsUnderline(element.isUnderline || false)
      setTextAlign(element.textAlign || TEXT_ALIGN.LEFT)
    }
  }, [
    element.text,
    element.fontSize,
    element.color,
    element.type,
    element.isBold,
    element.isItalic,
    element.isUnderline,
    element.textAlign,
    isEditing,
  ])

  // Effect to focus and select text when entering edit mode.
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.select() // Select existing text
    }
  }, [isEditing]) // Run only when isEditing state changes

  // --- Event Handlers ---

  /** Enters edit mode on click, unless readonly or clicking a resize handle. */
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (readonly || (e.target as HTMLElement).closest(".resize-zone")) {
        return
      }
      setIsEditing(true)
    },
    [readonly]
  )

  /** Saves changes made during editing back to the parent component via onUpdate. */
  const saveChanges = useCallback(() => {
    if (!isEditing) return
    onUpdate(element.id, {
      text,
      fontSize,
      color,
      isBold,
      isItalic,
      isUnderline,
      textAlign,
    })
    setIsEditing(false)
  }, [
    isEditing,
    onUpdate,
    element.id,
    text,
    fontSize,
    color,
    isBold,
    isItalic,
    isUnderline,
    textAlign,
  ])

  /** Handles blur event on the input field to save changes, unless focus moved to controls. */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null
      const isFocusWithinControls =
        relatedTarget &&
        (relatedTarget.closest(".text-element-controls") ||
          relatedTarget.closest(".ant-tooltip-inner") ||
          relatedTarget.closest(".ant-select-dropdown") ||
          relatedTarget.closest(".ant-color-picker-dropdown") ||
          relatedTarget.closest(".ant-radio-button-wrapper"))
      if (!isFocusWithinControls && isEditing) {
        saveChanges()
      } else if (colorPickerOpen && isFocusWithinControls) {
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    },
    [isEditing, saveChanges, colorPickerOpen]
  )

  /** Handles keydown events in the input field (Enter to save, Escape to cancel). */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        saveChanges()
      } else if (e.key === "Escape") {
        setText(element.text || "")
        setFontSize(
          element.fontSize || (element.type === ELEMENT_TYPE.HEADING ? 24 : 16)
        )
        setColor(element.color || "#000000")
        setIsBold(element.isBold || element.type === ELEMENT_TYPE.HEADING)
        setIsItalic(element.isItalic || false)
        setIsUnderline(element.isUnderline || false)
        setTextAlign(element.textAlign || TEXT_ALIGN.LEFT)
        setIsEditing(false)
      }
    },
    [saveChanges, element]
  )

  const handleFontSizeChange = useCallback((value: number) => {
    setFontSize(value)
  }, [])

  const handleColorChange = useCallback((colorValue: Color) => {
    setColor(colorValue.toHexString())
  }, [])

  const toggleBold = useCallback(() => {
    setIsBold((prev) => !prev)
  }, [])

  const toggleItalic = useCallback(() => {
    setIsItalic((prev) => !prev)
  }, [])

  const toggleUnderline = useCallback(() => {
    setIsUnderline((prev) => !prev)
  }, [])

  const handleAlignmentChange = useCallback((e: RadioChangeEvent) => {
    setTextAlign(e.target.value || TEXT_ALIGN.LEFT)
  }, [])

  const handleControlMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onDeleteRequest) {
        onDeleteRequest()
      } else {
        console.warn("onDeleteRequest not provided to TextElement")
      }
    },
    [onDeleteRequest]
  )

  // --- Styles ---
  const textStyles = useMemo(
    (): React.CSSProperties => ({
      fontSize: `${fontSize}px`,
      fontWeight: isBold ? "bold" : "normal",
      fontStyle: isItalic ? "italic" : "normal",
      textDecoration: isUnderline ? "underline" : "none",
      color: color,
      textAlign: textAlign as React.CSSProperties["textAlign"],
      lineHeight: 1.3,
      width: "100%",
      height: "100%",
      padding: "2px",
      margin: 0,
      boxSizing: "border-box",
      display: "block",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      outline: isEditing ? "1px solid #90cdf4" : "none", // Specific editing outline for text area
      outlineOffset: "1px",
      backgroundColor: isEditing ? "rgba(239, 246, 255, 0.5)" : "transparent",
    }),
    [fontSize, isBold, isItalic, isUnderline, color, textAlign, isEditing]
  )

  // Dynamic outline class for the wrapper, based on editing state
  const wrapperOutlineClass = isEditing
    ? "outline outline-2 outline-blue-500"
    : ""

  // Style for the wrapper related to cursor
  const wrapperStyleOverrides: React.CSSProperties = {
    cursor: readonly ? "default" : isEditing ? "text" : "pointer",
    width: "100%",
    height: "100%",
  }

  return (
    <ResizableElementWrapper
      element={element}
      onUpdate={onUpdate}
      readonly={readonly}
      minDimensions={{ width: 50, height: 20 }}
      defaultWidth={
        (
          DEFAULT_TEXT_SIZES as Record<
            string,
            { width: number; height: number }
          >
        )[element.type]?.width || 200
      }
      defaultHeight={
        (
          DEFAULT_TEXT_SIZES as Record<
            string,
            { width: number; height: number }
          >
        )[element.type]?.height || 40
      }
      onClick={handleClick}
      additionalClasses={`text-element-wrapper ${wrapperOutlineClass}`} // Pass text-element specific classes
      styleOverrides={wrapperStyleOverrides}
      ariaLabel={
        element.type === ELEMENT_TYPE.HEADING
          ? `Heading: ${text}`
          : `Text: ${text}`
      }
      ariaReadonly={readonly}
    >
      {/* Conditional Rendering: Edit Mode vs Display Mode */}
      {isEditing ? (
        <div className="relative w-full h-full text-element-editor">
          <div
            onMouseDown={handleControlMouseDown}
            className="text-element-controls absolute left-0 top-0 transform -translate-y-full bg-white shadow-md rounded border border-gray-200 flex items-center p-1 z-30 gap-1 flex-wrap"
          >
            <Select
              size="small"
              value={fontSize}
              onChange={handleFontSizeChange}
              style={{ width: 60, flexShrink: 0 }}
              bordered={false}
              dropdownMatchSelectWidth={false}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Font size"
            >
              {FONT_SIZE_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
            <div onMouseDown={handleControlMouseDown}>
              <ColorPicker
                size="small"
                value={color}
                onChange={handleColorChange}
                presets={COLOR_PRESETS}
                open={colorPickerOpen}
                onOpenChange={setColorPickerOpen}
                trigger="click"
                arrow={true}
                format="hex"
                getPopupContainer={() => document.body} // Ensures picker is not clipped
                aria-label="Text color"
              />
            </div>
            <div className="h-4 w-px bg-gray-200 mx-1"></div>
            <Tooltip title="Bold">
              <Button
                size="small"
                type={isBold ? "primary" : "text"}
                icon={<FontAwesomeIcon icon={faBold} />}
                onClick={toggleBold}
                onMouseDown={handleControlMouseDown}
                aria-pressed={isBold}
              />
            </Tooltip>
            <Tooltip title="Italic">
              <Button
                size="small"
                type={isItalic ? "primary" : "text"}
                icon={<FontAwesomeIcon icon={faItalic} />}
                onClick={toggleItalic}
                onMouseDown={handleControlMouseDown}
                aria-pressed={isItalic}
              />
            </Tooltip>
            <Tooltip title="Underline">
              <Button
                size="small"
                type={isUnderline ? "primary" : "text"}
                icon={<FontAwesomeIcon icon={faUnderline} />}
                onClick={toggleUnderline}
                onMouseDown={handleControlMouseDown}
                aria-pressed={isUnderline}
              />
            </Tooltip>
            <div className="h-4 w-px bg-gray-200 mx-1"></div>
            <div onMouseDown={handleControlMouseDown}>
              <Radio.Group
                onChange={handleAlignmentChange}
                value={textAlign}
                size="small"
                aria-label="Text alignment"
              >
                <Tooltip title="Align Left">
                  <Radio.Button value={TEXT_ALIGN.LEFT}>
                    <FontAwesomeIcon icon={faAlignLeft} />
                  </Radio.Button>
                </Tooltip>
                <Tooltip title="Align Center">
                  <Radio.Button value={TEXT_ALIGN.CENTER}>
                    <FontAwesomeIcon icon={faAlignCenter} />
                  </Radio.Button>
                </Tooltip>
                <Tooltip title="Align Right">
                  <Radio.Button value={TEXT_ALIGN.RIGHT}>
                    <FontAwesomeIcon icon={faAlignRight} />
                  </Radio.Button>
                </Tooltip>
              </Radio.Group>
            </div>
            <div className="h-4 w-px bg-gray-200 mx-1"></div>
            {onDeleteRequest && (
              <Tooltip title="Delete Element">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  onClick={handleDelete}
                  onMouseDown={handleControlMouseDown}
                  className="text-element-delete-button"
                  style={{ padding: "0 4px", border: "none" }}
                  aria-label="Delete element"
                />
              </Tooltip>
            )}
          </div>
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="bg-transparent focus:outline-none" // Basic styling for input
            style={textStyles}
            aria-label="Edit text content"
          />
        </div>
      ) : (
        <span
          style={textStyles}
          aria-label="Text content"
          className="w-full h-full block"
        >
          {text ||
            (readonly ? (
              ""
            ) : (
              <span className="text-gray-400 italic">Click to edit</span>
            ))}
        </span>
      )}
    </ResizableElementWrapper>
  )
}

export default TextElement
